import errorCodes from "@/apis/error";
import { orgFetchIds, orgUpdate, orgDisable, orgUpdatePid } from "@/apis/org";
import { Org, OrgUpdate } from "@/types/org";
import { notification } from "antd";
import { useState } from "react";
import { useTranslation } from 'react-i18next';

type Fetch = (id: string) => Promise<Org>;
type UpdateOrg = (id: string, data: OrgUpdate) => Promise<Org>;
type DisableOrg = (id: string) => Promise<void>;
type SetOrgDetail = (detail: Org) => void;
type UpdateOrgPId = (id: string, pId: string) => Promise<void>;

type IUseOrg = () => [
  Org | undefined, boolean, boolean,
  { fetch: Fetch, updateOrg: UpdateOrg, disableOrg: DisableOrg, setOrgDetail: SetOrgDetail, updateOrgPId: UpdateOrgPId }
]

const useOrg: IUseOrg = () => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<Org | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [disLoading, setDisLoading] = useState<boolean>(false);

  const setOrgDetail = (detail: Org) => setDetail(detail);

  const fetch: Fetch = (id) => {
    setLoading(true)
    return new Promise((resolve, reject) => {
      orgFetchIds([id])
        .then((res) => {
          setDetail(res[0])
          resolve(res[0])
        })
        .catch((err) => {
          notification.error({ placement: 'top', message: errorCodes[err] || err })
        })
        .finally(() => setLoading(false))
    })
  }

  const updateOrg: UpdateOrg = (id, data) => {
    setLoading(true)
    return new Promise((resolve, reject) => {
      orgUpdate(id, data)
        .then((res) => {
          resolve(res)
          notification.success({ placement: 'top', message: t('org.updateSuccess') })
        })
        .catch((error) => {
          notification.error({ placement: 'top', message: t('org.updateFailed'), description: errorCodes[error] || error })
        })
        .finally(() => setLoading(false))
    })
  }

  const disableOrg: DisableOrg = (id) => {
    setDisLoading(true)
    return new Promise((resolve, reject) => {
      orgDisable(id)
        .then(() => {
          resolve()
          notification.success({ placement: 'top', message: t('org.delOrgSuccess'), description: t('layout.switchOrg') })
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        })
        .catch((error) => {
          if (error === 70304) {
            notification.error({ placement: 'top', message: t('org.delOrgTip3') })
          } else if (error === 70301) {
            notification.error({ placement: 'top', message: t('apiError.error20') })
          } else {
            notification.error({ placement: 'top', message: t('org.delOrgFailed') })
          }
        }).finally(() => setDisLoading(false))
    })
  }

  const updateOrgPId: UpdateOrgPId = (id, pId) => {
    setLoading(true)
    return new Promise((resolve, reject) => {
      orgUpdatePid(id, pId)
        .then(() => {
          notification.success({ placement: 'top', message: t('org.orgMoveSuc') })
          resolve()
        })
        .catch((error) => {
          notification.error({ placement: 'top', message: t('org.orgMoveFail'), description: errorCodes[error] || error })
        })
        .finally(() => setLoading(false))
    })
  }

  return [detail, loading, disLoading, { fetch, updateOrg, disableOrg, setOrgDetail, updateOrgPId }]
}

export default useOrg;